import * as PIXI from 'pixi.js';
import { addNewRoofArea, getRoofOnMap, roofAreaMarkerLatLngs } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManager';
import { advanceRoofSelectingAlert, NEW_ROOF_INTERSECTION_ERROR_MSG } from '__editor/components/roofsSelector/components/roofsSelectorAdvanceRoofSelecting/roofsSelectorAdvanceRoofSelecting';
import { checkRoofSize } from '../drawingManager/utils/roofSizeAlert';
import { dispatch, state } from '__common/store';
import { getPixiLayer } from '__editor/googleMapsRoofsSelector/components/stage/stage';
import { newRoofOverlaping, clearRoof } from '../../../components/roofsSelector/components/roofsSelectorDrawingManager/roofsSelectorDrawingManager';
import { selectRoofLeadEdge, findSouthMostEdgeIndex } from '__editor/googleMapsRoofsSelector/components/leadEdgeRoofSelector/utils/selectEdge';
import { TERMINATE_ADVANCE_ROOF_SELECTING, TURN_OFF_MEASUREMENT_TOOL } from 'actions';
import { turnGoogleMapsOnMapMoveControls } from '__editor/googleMapsRoofsSelector/components/map/map';
import { deployAdvanceSelecting, getAdvanceRoofSelectingModel } from '__common/components/advancedSelecting/advanceSelecting';
import { getActiveSnappedCorner, clearActiveSnappedCorner, snapToRoofCorner } from './utils/roofVertexSnapping';
import { snapIndicatorModel } from '__editor/googleMapsRoofsSelector/models/snapIndicator';

let shiftPressed = false;

export const renderAdanvaceRoofSelecting = () => {
  const pixiLayer: any = getPixiLayer();
  if (!pixiLayer) {
    return;
  }
  pixiLayer.addLayer((layer: PIXI.Container) => {
    const scale = getPixiLayer().stage.scale.x;
    const model = getAdvanceRoofSelectingModel(scale);
    layer.id = 'adanvceRoofSelector';
    pixiLayer.stage.children = pixiLayer.stage.children.filter(child => child.id !== 'adanvceRoofSelector');

    // `model` is undefined until at least two points exist (e.g. while merely
    // hovering before the first click), but we still want to draw the snap
    // indicator on its own, so only add the line model when it exists.
    if (model) {
      layer.addChild(model);
    }

    const snappedCorner = getActiveSnappedCorner();
    if (snappedCorner) {
      layer.addChild(snapIndicatorModel(snappedCorner, scale));
    }
  });
};

// While the roof tool is active but before the first point is placed, show the
// potential snap target sitting under the cursor so the user can see where a
// click will land. Once drawing has started the regular render path (driven by
// ADD_ROOF_LAST_POINT) already shows the snapped point, so this only fills the
// pre-first-point hover gap.
export const renderRoofSnapHover = (renderer, stage, event?) => {
  const { advanceRoofSelecting: { enabled, roofPoints }, roofsSelector: { mapType } } = state();
  if (!enabled || mapType !== 'google' || roofPoints.length) {
    return;
  }
  // The interaction plugin does not always have `eventData` (notably during
  // initialization and for some touch/pointer events). Prefer the event that
  // triggered this render and only fall back to the plugin's cached event.
  let eventData = event && event.data;
  if (!eventData && renderer && renderer.plugins && renderer.plugins.interaction && renderer.plugins.interaction.eventData) {
    eventData = renderer.plugins.interaction.eventData.data;
  }
  if (!eventData || !eventData.global) {
    return;
  }
  const coords = stage.toLocal(eventData.global);
  snapToRoofCorner(coords);
  renderAdanvaceRoofSelecting();
};

export const shiftDown = () => {
  shiftPressed = true;
};

export const shiftUp = () => {
  shiftPressed = false;
};

export const isShiftPressed = () => shiftPressed;

export function deployAdvanceRoofSelecting(dblClick = false) {
  const pixiLayer = getPixiLayer();

  deployAdvanceSelecting({
    dblClick,
    selfIntersectionWarning: NEW_ROOF_INTERSECTION_ERROR_MSG,
    overlapingWarning: 'Roof cannot overlap another roof',
    onClear: () => clearGoogleAdvanceRoofSelecting(pixiLayer),
    overLapCheck: (roofPoints) => {
      const newRoofPoints = roofPoints.slice(0);

      const coords = newRoofPoints.map(coord => pixiLayer.fromPixelToLatLng(coord).toJSON());

      if (newRoofOverlaping(coords)) {
        advanceRoofSelectingAlert();
        clearGoogleAdvanceRoofSelecting(pixiLayer);
        return true;
      }
    },
    onDeploy: (newRoofPoints) => {
      if (newRoofPoints.length > 2) {
        const coords = newRoofPoints.map(coord => pixiLayer.fromPixelToLatLng(coord).toJSON());

        const { checkingMarker } = roofAreaMarkerLatLngs(coords);
        const roofAreaDeploy = () => {
            const roofId = addNewRoofArea(coords, newRoofPoints);
            setFirstDrawEdgeAsLeadEdge(roofId);
            const roof = getRoofOnMap(roofId);
            const leadEdgeCoords = [coords[0], coords[1]];
            const { lat, lng } = roof.roof.overlay.getRoofApproximateCenter();
            checkRoofSize(roofId, coords, { lat: lat(), lng: lng() }, leadEdgeCoords, 18);
            clearGoogleAdvanceRoofSelecting(pixiLayer);
          };
        clearRoof(checkingMarker, roofAreaDeploy);
      }
    },
  });
}

function setFirstDrawEdgeAsLeadEdge(roofId: number, coords: { lat: number, lng: number }[]) {
  const { app } = state();
  const edgeNumber = (app && app.isUbuilderLite) ? findSouthMostEdgeIndex(coords) : 0;
  selectRoofLeadEdge({ edgeNumber, roofId });
}

export function clearGoogleAdvanceRoofSelecting(pixiLayer) {
  clearActiveSnappedCorner();
  setTimeout(() => {
    dispatch(TERMINATE_ADVANCE_ROOF_SELECTING());
    dispatch(TURN_OFF_MEASUREMENT_TOOL());
    turnGoogleMapsOnMapMoveControls();

    pixiLayer.stage.children = pixiLayer.stage.children.filter(child => child.id !== 'adanvceRoofSelector');
  }, 30);
}
